// Comparativa interanual (p.ej. 2025 vs 2026). Años anteriores desde el
// histórico importado; año en curso desde el mercado vivo. Reutilizable en
// YTD Board y Business Review. Permite elegir qué dos años comparar cuando hay
// varios ejercicios, alternar entre nº de operaciones e importe (M€), y ver un
// mapa coroplético de España por provincia.
import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { BarChart3, Upload, Loader2, ArrowUpRight, ArrowDownRight, Map as MapIcon } from "lucide-react";
import { API } from "@/context/AuthContext";
import SpainProvinceMap from "@/components/historico/SpainProvinceMap";
import ProvinciaDetalleModal from "@/components/historico/ProvinciaDetalleModal";

const fmtMM = (v) => `${((v || 0) / 1e6).toLocaleString("es-ES", { maximumFractionDigits: 0 })} M€`;
const fmtN = (v) => (v || 0).toLocaleString("es-ES");
const fmtPct = (v) => (v == null ? "—" : `${(v * 100).toFixed(0)}%`);

function Delta({ cur, prev }) {
  if (!prev || prev === 0 || cur == null) return null;
  const pct = ((cur - prev) / prev) * 100;
  const up = pct >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold ${up ? "text-emerald-600" : "text-rose-600"}`}>
      {up ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}{Math.abs(pct).toFixed(0)}%
    </span>
  );
}

function Breakdown({ title, dim, shown, metric, testid }) {
  const val = (d) => (metric === "importe" ? d.importe : d.n) || 0;
  const totals = {};
  shown.forEach((s) => (s.agg[dim] || []).forEach((d) => { totals[d.k] = (totals[d.k] || 0) + val(d); }));
  const topKeys = Object.entries(totals).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([k]) => k);
  const look = (s, k) => { const e = (s.agg[dim] || []).find((d) => d.k === k); return e ? val(e) : 0; };
  const fmt = metric === "importe" ? fmtMM : fmtN;
  if (!topKeys.length) return null;
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase font-semibold text-gray-400 tracking-wide mb-1.5">{title}</div>
      <table className="w-full text-xs">
        <tbody>
          {topKeys.map((k) => (
            <tr key={k} className="border-b border-gray-50">
              <td className="py-1 pr-2 text-gray-600 truncate max-w-[180px]">{k}</td>
              {shown.map((s) => (
                <td key={s.ejercicio} className="py-1 px-2 text-right tabular-nums text-gray-800">{fmt(look(s, k))}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ComparativaAnualCard({ year, onImport, refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState([]);        // [añoA, añoB] elegidos
  const [metric, setMetric] = useState("n"); // "n" | "importe"
  const [ytd, setYtd] = useState(false);     // H03 · año completo vs YTD al mismo corte
  const [mapYear, setMapYear] = useState(null);
  const [mapMode, setMapMode] = useState("single"); // "single" | "side"
  const [detalle, setDetalle] = useState(null);      // {ejercicio, provincia}

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/historico/comparativa`, { params: { year_actual: year } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [year, refreshKey]);
  useEffect(() => { load(); }, [load]);

  const series = data?.series || [];
  const years = useMemo(() => series.map((s) => s.ejercicio).sort((a, b) => a - b), [series]);

  useEffect(() => {
    if (years.length >= 2) setSel([years[years.length - 2], years[years.length - 1]]);
    else setSel(years.slice());
  }, [years.join(",")]);

  const multi = years.length > 2;
  const shownRaw = (multi ? sel : years).map((y) => series.find((s) => s.ejercicio === y)).filter(Boolean);
  const shownSorted = shownRaw
    .filter((s, i, arr) => arr.findIndex((x) => x.ejercicio === s.ejercicio) === i)
    .sort((a, b) => a.ejercicio - b.ejercicio);
  // H03 · si el usuario pide YTD, usamos el agregado al mismo corte (agg_ytd)
  const shown = shownSorted.map((s) => ({ ...s, agg: (ytd && s.agg_ytd) ? s.agg_ytd : s.agg }));
  const comp = data?.comparabilidad;
  const loYear = shown[0];
  const hiYear = shown[shown.length - 1];
  const showDelta = shown.length >= 2 && loYear && hiYear && loYear.ejercicio !== hiYear.ejercicio;

  // Año activo para el mapa (entre los mostrados); por defecto el más reciente
  const effMapYear = mapYear && shown.some((s) => s.ejercicio === mapYear) ? mapYear : hiYear?.ejercicio;
  const mapSeries = shown.find((s) => s.ejercicio === effMapYear);
  const valueByProvince = {};
  (mapSeries?.agg?.por_provincia || []).forEach((d) => {
    valueByProvince[d.k] = metric === "importe" ? d.importe : d.n;
  });
  const metricLabel = metric === "importe" ? "importe (M€)" : "nº operaciones";
  const mapFormat = metric === "importe" ? fmtMM : fmtN;
  const provValues = (s) => {
    const o = {};
    (s?.agg?.por_provincia || []).forEach((d) => { o[d.k] = metric === "importe" ? d.importe : d.n; });
    return o;
  };
  const sharedMax = Math.max(1, ...shown.flatMap((s) => (s.agg.por_provincia || []).map((d) => (metric === "importe" ? d.importe : d.n))));

  const METRICS = [
    { key: "n", label: "Nº operaciones", fmt: fmtN, get: (a) => a.n },
    { key: "importe_total", label: "Importe total", fmt: fmtMM, get: (a) => a.importe_total },
    { key: "importe_medio", label: "Importe medio", fmt: fmtMM, get: (a) => a.importe_medio },
    { key: "vsm", label: "Participación VSM media", fmt: fmtPct, get: (a) => a.participacion_vsm_media },
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm" data-testid="comparativa-anual-card">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
            <BarChart3 size={16} className="text-indigo-700" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900">Comparativa interanual</h3>
            <p className="text-[11px] text-gray-500">Años anteriores desde histórico importado · año en curso desde mercado vivo</p>
          </div>
        </div>
        {onImport && (
          <button onClick={onImport} data-testid="comparativa-import-btn"
            className="shrink-0 flex items-center gap-1.5 text-[11px] font-semibold border border-gray-300 text-gray-700 rounded-lg px-2.5 py-1.5 hover:bg-gray-50">
            <Upload size={12} /> Importar año anterior
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-xs text-gray-400 py-8 text-center"><Loader2 className="animate-spin inline mr-2" size={14} />Cargando…</div>
      ) : !data?.tiene_historico ? (
        <div className="text-xs text-gray-500 py-6 text-center border border-dashed border-gray-200 rounded-lg" data-testid="comparativa-empty">
          Aún no hay años anteriores importados. Sube el Excel de un ejercicio (p.ej. 2025) con «Importar año anterior» para ver la comparativa.
        </div>
      ) : (
        <div className="space-y-4">
          {/* H03 · Dictamen de comparabilidad: nunca presentar el Δ como homogéneo sin acreditarlo */}
          {comp && comp.nivel === "orientativa" && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/70 p-2.5 text-[11px]" data-testid="comparativa-aviso">
              <p className="font-bold text-amber-800 mb-1">⚠️ Comparación orientativa · cobertura no equivalente</p>
              <ul className="list-disc pl-4 space-y-0.5 text-amber-900">
                {comp.motivos.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
              <p className="mt-1 text-amber-700">{comp.recomendacion}</p>
            </div>
          )}
          {comp && comp.nivel === "homogenea" && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-2 text-[11px] text-emerald-800" data-testid="comparativa-aviso">
              ✓ Comparación homogénea al corte {comp.corte}: mismo periodo, base y cobertura equivalente.
            </div>
          )}

          {/* Controles: selector de años (si hay varios) + toggle de métrica + YTD */}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            {multi ? (
              <div className="flex items-center gap-2 text-xs" data-testid="comparativa-year-selector">
                <span className="text-gray-500">Comparar</span>
                <select value={sel[0] ?? ""} onChange={(e) => setSel([Number(e.target.value), sel[1]])} data-testid="comparativa-year-a"
                  className="border border-gray-200 rounded-lg px-2 py-1 font-semibold text-gray-800">
                  {years.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
                <span className="text-gray-500">con</span>
                <select value={sel[1] ?? ""} onChange={(e) => setSel([sel[0], Number(e.target.value)])} data-testid="comparativa-year-b"
                  className="border border-gray-200 rounded-lg px-2 py-1 font-semibold text-gray-800">
                  {years.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            ) : <span />}
            <div className="flex items-center gap-2">
              <div className="flex rounded-lg border border-gray-200 overflow-hidden" data-testid="comparativa-cut-toggle">
                {[["full", "Año completo"], ["ytd", "YTD mismo corte"]].map(([m, label]) => (
                  <button key={m} onClick={() => setYtd(m === "ytd")} data-testid={`comparativa-cut-${m}`}
                    className={`text-[10px] font-semibold px-2.5 py-1 transition-colors ${(ytd ? "ytd" : "full") === m ? "bg-amber-600 text-white" : "bg-white text-gray-500 hover:bg-gray-50"}`}>
                    {label}
                  </button>
                ))}
              </div>
              <div className="flex rounded-lg border border-gray-200 overflow-hidden" data-testid="comparativa-metric-toggle">
                {[["n", "Nº operaciones"], ["importe", "Importe (M€)"]].map(([m, label]) => (
                  <button key={m} onClick={() => setMetric(m)} data-testid={`comparativa-metric-${m}`}
                    className={`text-[10px] font-semibold px-2.5 py-1 transition-colors ${metric === m ? "bg-indigo-600 text-white" : "bg-white text-gray-500 hover:bg-gray-50"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-gray-400 border-b border-gray-100">
                  <th className="py-1.5 pr-2 font-medium">Métrica</th>
                  {shown.map((s) => (
                    <th key={s.ejercicio} className="py-1.5 px-2 font-bold text-gray-700 text-right" data-testid={`comparativa-col-${s.ejercicio}`}>
                      {s.ejercicio}
                      <span className="block text-[9px] font-normal text-gray-400">{s.source === "live" ? "en vivo" : "histórico"}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {METRICS.map((m) => (
                  <tr key={m.key} className="border-b border-gray-50" data-testid={`comparativa-row-${m.key}`}>
                    <td className="py-1.5 pr-2 text-gray-600">{m.label}</td>
                    {shown.map((s) => (
                      <td key={s.ejercicio} className="py-1.5 px-2 text-right font-semibold text-gray-900 tabular-nums">
                        {m.fmt(m.get(s.agg))}
                        {showDelta && s.ejercicio === hiYear.ejercicio && m.key !== "vsm" && (
                          <span className="ml-1"><Delta cur={m.get(s.agg)} prev={m.get(loYear.agg)} /></span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
                {/* H03 · cobertura por dimensión (ausencia de dato ≠ cero actividad) */}
                {["con_provincia_pct", "con_dg_pct", "con_fecha_pct"].map((ck, i) => (
                  <tr key={ck} className="border-b border-gray-50 bg-gray-50/40" data-testid={`comparativa-cobertura-${ck}`}>
                    <td className="py-1 pr-2 text-[10px] text-gray-400">
                      {["Cobertura provincia", "Cobertura Dir. General", "Cobertura fecha"][i]}
                    </td>
                    {shown.map((s) => {
                      const v = s.agg?.cobertura?.[ck];
                      return (
                        <td key={s.ejercicio} className={`py-1 px-2 text-right text-[10px] tabular-nums ${v != null && v < 80 ? "text-amber-600 font-semibold" : "text-gray-400"}`}>
                          {v == null ? "—" : `${v}%`}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Breakdown title={`Por Dirección General (${metricLabel})`} dim="por_dg" shown={shown} metric={metric} testid="comparativa-breakdown-dg" />
          <Breakdown title={`Por provincia (${metricLabel})`} dim="por_provincia" shown={shown} metric={metric} testid="comparativa-breakdown-provincia" />

          {/* Mapa coroplético por provincia */}
          <div data-testid="comparativa-map-section">
            <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
              <div className="text-[10px] uppercase font-semibold text-gray-400 tracking-wide flex items-center gap-1.5">
                <MapIcon size={12} /> Mapa por provincia · {metricLabel}
              </div>
              <div className="flex items-center gap-2">
                {shown.length > 1 && (
                  <div className="flex rounded-lg border border-gray-200 overflow-hidden" data-testid="comparativa-map-mode">
                    {[["single", "Único"], ["side", "Lado a lado"]].map(([m, l]) => (
                      <button key={m} onClick={() => setMapMode(m)} data-testid={`comparativa-map-mode-${m}`}
                        className={`text-[10px] font-semibold px-2.5 py-1 transition-colors ${mapMode === m ? "bg-indigo-600 text-white" : "bg-white text-gray-500 hover:bg-gray-50"}`}>{l}</button>
                    ))}
                  </div>
                )}
                {mapMode === "single" && shown.length > 1 && (
                  <div className="flex rounded-lg border border-gray-200 overflow-hidden" data-testid="comparativa-map-year-pills">
                    {shown.map((s) => (
                      <button key={s.ejercicio} onClick={() => setMapYear(s.ejercicio)} data-testid={`comparativa-map-year-${s.ejercicio}`}
                        className={`text-[10px] font-semibold px-2.5 py-1 transition-colors ${effMapYear === s.ejercicio ? "bg-[#007934] text-white" : "bg-white text-gray-500 hover:bg-gray-50"}`}>{s.ejercicio}</button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <p className="text-[10px] text-gray-400 mb-1.5">Haz clic en una provincia para ver sus licitaciones de ese año.</p>
            {mapMode === "side" && shown.length > 1 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="comparativa-maps-side">
                {shown.map((s) => (
                  <div key={s.ejercicio} data-testid={`comparativa-map-${s.ejercicio}`}>
                    <div className="text-[11px] font-bold text-gray-700 text-center mb-1">
                      {s.ejercicio} <span className="font-normal text-gray-400">{s.source === "live" ? "· en vivo" : "· histórico"}</span>
                    </div>
                    <SpainProvinceMap valueByProvince={provValues(s)} format={mapFormat} maxValue={sharedMax}
                      metricLabel={`${s.ejercicio} · ${metricLabel}`}
                      onProvinceClick={(prov) => setDetalle({ ejercicio: s.ejercicio, provincia: prov })} />
                  </div>
                ))}
              </div>
            ) : (
              <SpainProvinceMap valueByProvince={valueByProvince} format={mapFormat}
                metricLabel={`${effMapYear} · ${metricLabel}`}
                onProvinceClick={(prov) => setDetalle({ ejercicio: effMapYear, provincia: prov })} />
            )}
          </div>

          {detalle && (
            <ProvinciaDetalleModal ejercicio={detalle.ejercicio} provincia={detalle.provincia}
              yearActual={year} onClose={() => setDetalle(null)} />
          )}

          <p className="text-[10px] text-gray-400">
            Importe = presupuesto total de licitación. {ytd ? "Vista YTD al mismo corte." : "Vista año completo."}
            {showDelta ? ` Δ compara ${hiYear.ejercicio} frente a ${loYear.ejercicio}` : ""}
            {showDelta && comp?.nivel === "orientativa" ? " — orientativo (cobertura/corte no equivalentes, ver aviso)." : showDelta ? "." : ""}
          </p>
        </div>
      )}
    </div>
  );
}
